/**
 * Google encoded-polyline codec.
 *
 * Mapbox returns precision 6 (`geometries=polyline6`); most GPX/third-party
 * tooling assumes precision 5. Both are supported via the `precision` argument.
 * We persist precision 6 — at precision 5 the quantisation error is ~1.1 m,
 * which is enough to make off-route detection twitchy on narrow lanes.
 */

const DEFAULT_PRECISION = 6;

export function decodePolyline(encoded: string, precision = DEFAULT_PRECISION): Array<[number, number]> {
  const factor = 10 ** precision;
  const coords: Array<[number, number]> = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    // Emitted as [lng, lat] to match GeoJSON, though the wire format is lat-first.
    coords.push([lng / factor, lat / factor]);
  }

  return coords;
}

export function encodePolyline(coords: Array<[number, number]>, precision = DEFAULT_PRECISION): string {
  const factor = 10 ** precision;
  let output = '';
  let prevLat = 0;
  let prevLng = 0;

  for (const [lng, lat] of coords) {
    const iLat = Math.round(lat * factor);
    const iLng = Math.round(lng * factor);
    output += encodeSignedNumber(iLat - prevLat);
    output += encodeSignedNumber(iLng - prevLng);
    prevLat = iLat;
    prevLng = iLng;
  }

  return output;
}

function encodeSignedNumber(value: number): string {
  let v = value < 0 ? ~(value << 1) : value << 1;
  let output = '';
  while (v >= 0x20) {
    output += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
    v >>= 5;
  }
  output += String.fromCharCode(v + 63);
  return output;
}
